import React, { useState } from "react";
import {
  Autocomplete,
  FormControl,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { ChevronIcon, TrashIcon } from "../icons/FormIcons";
import FormField from "./FormField";

const circleFlagCodeMap = {
  "AFGHANISTAN": "af",
  "ALGERIA": "dz",
  "BAHRAIN": "bh",
  "DJIBOUTI": "dj",
  "EGYPT": "eg",
  "IRAN": "ir",
  "IRAQ": "iq",
  "JORDAN": "jo",
  "KUWAIT": "kw",
  "LEBANON": "lb",
  "LIBYA": "ly",
  "MAURITANIA": "mr",
  "MOROCCO": "ma",
  "OMAN": "om",
  "PAKISTAN": "pk",
  "PALESTINE": "ps",
  "QATAR": "qa",
  "SAUDI ARABIA": "sa",
  "SOMALIA": "so",
  "SUDAN": "sd",
  "SYRIA": "sy",
  "TUNISIA": "tn",
  "TURKEY": "tr",
  "UAE": "ae",
  "UAE - ABU DHABI": "ae",
  "UAE - AJMAN": "ae",
  "UAE - DUBAI": "ae",
  "UAE - FUJAIRAH": "ae",
  "UAE - RAK": "ae",
  "UAE - SHARJAH": "ae",
  "UAE - UAQ": "ae",
  "YEMEN": "ye",
};

const getCircleFlagCode = (country) => {
  if (!country) return "xx";
  const normalized = country.trim().toUpperCase();
  if (/^[A-Z]{2}$/.test(normalized)) return normalized.toLowerCase();
  return circleFlagCodeMap[normalized] || "xx";
};

const getCircleFlagUrl = (country) =>
  `https://hatscripts.github.io/circle-flags/flags/${getCircleFlagCode(
    country
  )}.svg`;

const CountryFlagIcon = ({ country, size = 22, className = "" }) => (
  <img
    src={getCircleFlagUrl(country)}
    alt={country ? `${country} flag` : "Flag"}
    width={size}
    height={size}
    className={`flex-shrink-0 ${className}`}
    style={{
      borderRadius: "50%",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
      border: "1px solid rgba(15, 23, 42, 0.08)",
      backgroundColor: "#fff",
    }}
    loading="lazy"
  />
);

const TaskCard = ({
  task,
  index,
  onChange,
  onRemove,
  taskOptions,
  countryOptions,
  projectManagerOptions,
  company,
  vatLocked = false,
  showTaskStatus = false,
  defaultExpanded,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? index === 0);
  const showAedFields = company !== "UK";

  const dropdownMenuProps = {
    PaperProps: {
      sx: {
        borderRadius: "1rem",
        marginTop: "0.5rem",
        boxShadow:
          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        "& .MuiList-root": { padding: "0.5rem" },
        "& .MuiMenuItem-root": {
          borderRadius: "0.5rem",
          marginBottom: "0.25rem",
          "&:last-child": { marginBottom: 0 },
          "&.Mui-selected": {
            backgroundColor: "#e0e7ff",
            color: "#4f46e5",
            fontWeight: 600,
            "&:hover": { backgroundColor: "#c7d2fe" },
          },
          "&:hover": { backgroundColor: "#f3f4f6" },
        },
      },
    },
  };

  const autocompleteProps = {
    paper: {
      sx: {
        borderRadius: "1rem",
        marginTop: "0.5rem",
        boxShadow:
          "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        "& .MuiAutocomplete-listbox": { padding: "0.5rem" },
        "& .MuiAutocomplete-option": {
          borderRadius: "0.5rem",
          marginBottom: "0.25rem",
          "&:last-child": { marginBottom: 0 },
          '&[aria-selected="true"]': {
            backgroundColor: "#e0e7ff !important",
            color: "#4f46e5",
            fontWeight: 600,
          },
          "&.Mui-focused": { backgroundColor: "#f3f4f6 !important" },
        },
      },
    },
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="px-6 py-4 bg-gray-50 flex items-center justify-between cursor-pointer hover:bg-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold text-sm">
            {task.taskNo}
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {task.subject || `Task ${task.taskNo}`}
            </p>
            <p className="text-xs text-gray-500">
              {task.country && task.task
                ? `${task.country} • ${task.task}`
                : task.country || task.task || 'Not configured'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {task.amountIncVat && parseFloat(task.amountIncVat) > 0 && (
            <span className="text-sm font-medium text-green-600">
              £{parseFloat(task.amountIncVat).toFixed(2)}
            </span>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors duration-200"
            title="Remove Task"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
          <ChevronIcon
            className={`w-5 h-5 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
              }`}
          />
        </div>
      </div>

      {isExpanded && (
        <div className="p-6 space-y-4 bg-white animate-fadeIn">
          {/* Row 1: Task Number, Subject, Country */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Task No">
              <input
                type="text"
                value={task.taskNo}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-100 text-gray-700 cursor-not-allowed text-sm"
              />
            </FormField>

            <FormField label="Subject">
              <input
                type="text"
                name="subject"
                value={task.subject}
                onChange={(e) => onChange(index, e)}
                placeholder="Enter subject..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                         focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                         hover:border-gray-400 transition-all duration-200"
              />
            </FormField>

            <FormField label="Country">
              <Autocomplete
                componentsProps={autocompleteProps}
                options={countryOptions}
                value={task.country}
                onChange={(event, newValue) => onChange(index, { target: { name: 'country', value: newValue } })}
                renderOption={(props, option) => (
                  <li {...props} className={`${props.className} flex items-center gap-3`}>
                    <CountryFlagIcon country={option} size={22} />
                    <span>{option}</span>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select country..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.75rem',
                        padding: '0',
                        '& fieldset': { borderColor: '#d1d5db' },
                        '&:hover fieldset': { borderColor: '#9ca3af' },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6366f1',
                          borderWidth: '2px',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '12px 16px',
                          fontSize: '0.875rem',
                        },
                      },
                    }}
                  />
                )}
              />
            </FormField>
          </div>

          {/* Row 2: Task, Analyst 1, Analyst 2 */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Task">
              <Autocomplete
                componentsProps={autocompleteProps}
                options={taskOptions}
                value={task.task}
                onChange={(event, newValue) => onChange(index, { target: { name: 'task', value: newValue } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select task..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.75rem',
                        padding: '0',
                        '& fieldset': { borderColor: '#d1d5db' },
                        '&:hover fieldset': { borderColor: '#9ca3af' },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6366f1',
                          borderWidth: '2px',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '12px 16px',
                          fontSize: '0.875rem',
                        },
                      },
                    }}
                  />
                )}
              />
            </FormField>

            <FormField label="Analyst 1">
              <Autocomplete
                componentsProps={autocompleteProps}
                options={projectManagerOptions}
                value={task.analyst1}
                onChange={(event, newValue) => onChange(index, { target: { name: 'analyst1', value: newValue } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select analyst..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.75rem',
                        padding: '0',
                        '& fieldset': { borderColor: '#d1d5db' },
                        '&:hover fieldset': { borderColor: '#9ca3af' },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6366f1',
                          borderWidth: '2px',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '12px 16px',
                          fontSize: '0.875rem',
                        },
                      },
                    }}
                  />
                )}
              />
            </FormField>

            <FormField label="Analyst 2">
              <Autocomplete
                componentsProps={autocompleteProps}
                options={projectManagerOptions}
                value={task.analyst2}
                onChange={(event, newValue) => onChange(index, { target: { name: 'analyst2', value: newValue } })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select analyst..."
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '0.75rem',
                        padding: '0',
                        '& fieldset': { borderColor: '#d1d5db' },
                        '&:hover fieldset': { borderColor: '#9ca3af' },
                        '&.Mui-focused fieldset': {
                          borderColor: '#6366f1',
                          borderWidth: '2px',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '12px 16px',
                          fontSize: '0.875rem',
                        },
                      },
                    }}
                  />
                )}
              />
            </FormField>
          </div>

          {/* Row 3: Order Accepted, Due Date, Completed On */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Order Accepted">
              <input
                type="date"
                name="orderAccepted"
                value={task.orderAccepted}
                onChange={(e) => onChange(index, e)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                         focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                         hover:border-gray-400 transition-all duration-200"
              />
            </FormField>

            <FormField label="Due Date">
              <input
                type="date"
                name="dueDate"
                value={task.dueDate}
                onChange={(e) => onChange(index, e)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                         focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                         hover:border-gray-400 transition-all duration-200"
              />
            </FormField>

            <FormField label="Completed On">
              <input
                type="date"
                name="completedOn"
                value={task.completedOn}
                onChange={(e) => onChange(index, e)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                         focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                         hover:border-gray-400 transition-all duration-200"
              />
            </FormField>
          </div>

          {showTaskStatus && (
            <div className="grid grid-cols-1 gap-4">
              <FormField label="Task Status">
                <input
                  type="text"
                  name="taskStatus"
                  value={task.taskStatus}
                  onChange={(e) => onChange(index, e)}
                  placeholder="e.g., In Progress, Completed..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                           focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                           hover:border-gray-400 transition-all duration-200"
                />
              </FormField>
            </div>
          )}

          {/* Row 4: Fee (GBP), VAT, Amount Inc VAT */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField label="Fee (GBP)">
              <input
                type="text"
                name="income"
                value={task.income}
                onChange={(e) => onChange(index, e)}
                placeholder="0.00"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                         focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                         hover:border-gray-400 transition-all duration-200"
              />
            </FormField>

            <FormField label="VAT">
              <FormControl fullWidth>
                <Select
                  MenuProps={dropdownMenuProps}
                  name="vat"
                  value={task.vat}
                  onChange={(e) => onChange(index, e)}
                  disabled={vatLocked}
                  className="rounded-xl"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#d1d5db',
                      borderRadius: '0.75rem',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#9ca3af',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#6366f1',
                      borderWidth: '2px',
                    },
                    '& .MuiSelect-select': {
                      padding: '12px 16px',
                      fontSize: '0.875rem',
                    },
                  }}
                >
                  <MenuItem value={0}>0%</MenuItem>
                  <MenuItem value={5}>5%</MenuItem>
                  <MenuItem value={20}>20%</MenuItem>
                </Select>
              </FormControl>
            </FormField>

            <FormField label="Fee Inc VAT (GBP)">
              <div className="relative">
                <input
                  type="text"
                  value={task.amountIncVat}
                  disabled
                  className="w-full px-4 py-3 pr-20 border border-green-200 rounded-xl bg-green-50 text-green-900 cursor-not-allowed text-sm"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                  Calc
                </span>
              </div>
            </FormField>
          </div>

          {showAedFields && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField label="Fee (AED) - Optional">
                <input
                  type="text"
                  name="incomeAED"
                  value={task.incomeAED}
                  onChange={(e) => onChange(index, e)}
                  placeholder="0.00"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm text-gray-900
                           focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                           hover:border-gray-400 transition-all duration-200"
                />
              </FormField>

              <FormField label="VAT (AED)">
                <FormControl fullWidth>
                  <Select
                    MenuProps={dropdownMenuProps}
                    name="vatAED"
                    value={task.vatAED}
                    onChange={(e) => onChange(index, e)}
                    disabled={vatLocked}
                    className="rounded-xl"
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#d1d5db',
                        borderRadius: '0.75rem',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#9ca3af',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#6366f1',
                        borderWidth: '2px',
                      },
                      '& .MuiSelect-select': {
                        padding: '12px 16px',
                        fontSize: '0.875rem',
                      },
                    }}
                  >
                    <MenuItem value={0}>0%</MenuItem>
                    <MenuItem value={5}>5%</MenuItem>
                    <MenuItem value={20}>20%</MenuItem>
                  </Select>
                </FormControl>
              </FormField>

              <FormField label="Fee Inc VAT (AED)">
                <div className="relative">
                  <input
                    type="text"
                    value={task.amountIncVatAED}
                    disabled
                    className="w-full px-4 py-3 pr-20 border border-green-200 rounded-xl bg-green-50 text-green-900 cursor-not-allowed text-sm"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                    Calc
                  </span>
                </div>
              </FormField>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TaskCard;
